<template>
    <div class="ingredient-list" ref="ingredientList">
        <div v-for="ingredient in ingredients" :key="ingredient.id">
            <div :id="'ingredient-'+ ingredient.id" class="align-middle grid-x ingredient-item" :dusk="'ingredient-item-' + ingredient.id" @click="(isAvailable(ingredient) && type === IngredientListType.MULTI && !ingredient.quantifiable) ? toggleIngredient(ingredient) : ((isAvailable(ingredient) && type === IngredientListType.SINGLE) ? selectIngredient(ingredient) : false)">
                <div class="cell auto customizer-padding">
                    <p :class="['ingredient-name', {selected: isSelected(ingredient)}, {disabled: isLabelDisabled(ingredient)}]">{{ ingredient.name + ((ingredient.price) ? ` (${parsePrice(ingredient.price)} extra)` : '') }}</p>
                </div>
                <div v-if="!isAvailable(ingredient)" class="text-right cell shrink">
                    <p class="color-battleship-gray display-small margin-0 unavailability-reason">{{ unavailabilityReason(ingredient) }}</p>
                </div>
                <div v-else class="cell shrink">
                    <checkbox
                        v-if="type === IngredientListType.MULTI && !ingredient.quantifiable"
                        class="ingredient-toggle"
                        :checked="isSelected(ingredient)"
                        :disabled="!isSelected(ingredient) && isStepFull"
                        @change="toggleIngredient(ingredient)"
                    ></checkbox>
                    <number-input
                        v-if="type === IngredientListType.MULTI && ingredient.quantifiable"
                        class="ingredient-quantity"
                        :value="ingredientQuantity(ingredient)"
                        :minimum-amount="0"
                        :allow-positive="!isStepFull"
                        @change="setIngredientQuantity($event.value, ingredient)"
                    ></number-input>
                    <checkbox
                        v-else-if="type === IngredientListType.SINGLE"
                        class="ingredient-toggle"
                        :radio="true"
                        :checked="isSelected(ingredient)"
                        @change="selectIngredient(ingredient)"
                    ></checkbox>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Ingredient from '@/models/ingredient';
import Checkbox from '@/components/common/ui/Checkbox.vue';
import ProductCustomizerModule from '@/stores/modules/product-customizer';
import { getModule } from 'vuex-module-decorators';
import Step from '@/models/step';
import { IngredientListType } from '@/utils/types';
import NumberInput from '@/components/common/forms/NumberInput.vue';
import TimeSchedulerModule from '@/stores/modules/time-scheduler';
import { printPrice } from '@/utils/utilities';

@Component({
    components: { Checkbox, NumberInput }
})
export default class IngredientList extends Vue
{
    @Prop(Array) readonly ingredients!: Ingredient[];
    @Prop({type: String, default: IngredientListType.MULTI}) readonly type!: IngredientListType;
    @Prop(Number) readonly parentOffset!: number;
    @Prop(Object) readonly step!: Step;

    productCustomizerModule!: ProductCustomizerModule;
    timeSchedulerModule!: TimeSchedulerModule;
    containerHeight: string = '0px';

    created() {
        this.productCustomizerModule = getModule(ProductCustomizerModule, this.$store);
        this.timeSchedulerModule = getModule(TimeSchedulerModule, this.$store);
    }

    setIngredientQuantity(quantity: number, ingredient: Ingredient) {
        const previousQuantity = this.ingredientQuantity(ingredient);
        if (!this.isStepFull || (previousQuantity >= 1 && quantity < previousQuantity)) {
            ingredient.quantity = quantity;
            if (this.isSelected(ingredient)) {
                if (!quantity) {
                    return this.productCustomizerModule.removeIngredient([this.step, ingredient]);
                }
                return this.productCustomizerModule.updateIngredient([this.step, new Ingredient(ingredient)]);
            }
            this.productCustomizerModule.addIngredient([this.step, ingredient]);
        }
    }

    toggleIngredient(ingredient: Ingredient) {
        this.productCustomizerModule.setDirty(true);
        if (this.isSelected(ingredient)) {
            this.productCustomizerModule.removeIngredient([this.step, ingredient]);
            return;
        }
        if (!this.isStepFull) {
            ingredient.quantity = 1;
            this.productCustomizerModule.addIngredient([this.step, ingredient]);
        }
    }

    selectIngredient(ingredient: Ingredient) {
        this.productCustomizerModule.setDirty(true);
        ingredient.quantity = 1;
        this.productCustomizerModule.clearIngredients(this.step);
        this.productCustomizerModule.addIngredient([this.step, ingredient]);
    }

    isAvailable(ingredient: Ingredient) {
        if (!ingredient.in_stock) return false;
        if (ingredient.time_start && ingredient.time_end) {
            const selectedTimeSlot = this.timeSchedulerModule.selectedTimeSlot!.start();
            if (selectedTimeSlot > ingredient.time_end || selectedTimeSlot < ingredient.time_start) return false;
        }
        return true;
    }

    unavailabilityReason(ingredient: Ingredient) {
        if (!ingredient.in_stock) return 'Out of stock';
        if (ingredient.time_start && ingredient.time_end) {
            const selectedTimeSlot = this.timeSchedulerModule.selectedTimeSlot!.start();
            if (selectedTimeSlot > ingredient.time_end || selectedTimeSlot < ingredient.time_start) {
                const timeStart = ingredient.time_start.toLocaleString('en-US', {hour: 'numeric', minute: 'numeric', hour12: true});
                const timeEnd = ingredient.time_end.toLocaleString('en-US', {hour: 'numeric', minute: 'numeric', hour12: true});
                return `Only available between ${timeStart} and ${timeEnd}`;
            }
        }
        return '';
    }

    isSelected(ingredient: Ingredient) {
        return this.productCustomizerModule.isIngredientSelected([this.step, ingredient]);
    }

    ingredientQuantity(ingredient: Ingredient) {
        return this.productCustomizerModule.ingredientQuantity([this.step, ingredient]);
    }

    get ingredientsCount() {
        return this.productCustomizerModule.stepIngredientsCount(this.step);
    }

    get isStepFull() {
        return this.step.max_items > 0 && this.ingredientsCount === this.step.max_items;
    }

    get isLabelDisabled() {
        return (ingredient: Ingredient) => !this.isSelected(ingredient) && this.isStepFull && this.type === IngredientListType.MULTI && !ingredient.quantifiable;
    }

    get IngredientListType() {
        return IngredientListType;
    }

    get parsePrice() {
        return (price: number) => printPrice(price);
    }
}
</script>
<style lang="scss">
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.ingredient-list {
    position: static;
    overflow-y: auto;
    width: 100%;
    padding-bottom: 0px;

    .ingredient-name {
        margin: 10px 0;
        color: var(--customizer-option-color);

        &.disabled {
            color: var(--customizer-option-disabled-color);
        }

        &.selected {
            font-weight: 600;
            color: var(--customizer-option-selected-color);
        }
    }



    .ingredient-item {
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        cursor: pointer;

        .ingredient-toggle {
            padding-right: 15px;
        }

        .ingredient-quantity {
            margin-right: 12.5px;
        }
    }

    .unavailability-reason {
        padding-right: 25px;
    }
}

</style>

